import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _styled from "@emotion/styled-base";
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { themeGet } from '@styled-system/theme-get';
import { compose, space, flexbox, typography, color, border, position, shadow, layout } from 'styled-system';
import Spinner from './Spinner';
import Box from './Box';
import { getStyledFunctionsNames, shouldForwardProp as _shouldForwardProp, values } from './utils';
var VARIANTS = {
  DEFAULT: 'default',
  PRIMARY: 'primary',
  OUTLINE: 'outline',
  TRANSPARENT: 'transparent',
  DESTRUCTIVE: 'destructive',
  LINK: 'link',
  SUBTLE: 'subtle'
};
var SIZES = {
  MINI: 'mini',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large'
};
var INTERACTION_TRANSITION = 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1) 0s';
var HOVER_OPACITY = 0.9;
var DISABLED_OPACITY = 0.5;
var styledFunctions = {
  layout: layout,
  space: space,
  flexbox: flexbox,
  typography: typography,
  color: color,
  border: border,
  position: position,
  shadow: shadow
};

var BaseButton =
/*#__PURE__*/
_styled('button', {
  shouldForwardProp: function shouldForwardProp(prop) {
    return _shouldForwardProp(prop, styledFunctions);
  },
  target: "e1xldxvx0"
})(compose.apply(void 0, _toConsumableArray(values(styledFunctions))), " position:relative;border-style:solid;touch-action:manipulation;transition:", INTERACTION_TRANSITION, ";&[disabled]{opacity:", DISABLED_OPACITY, ";cursor:not-allowed;}&[href]{text-decoration:", function (props) {
  return props.textDecoration;
}, ";}&:hover&:not(:disabled){opacity:", HOVER_OPACITY, ";cursor:pointer;background-color:", function (props) {
  return themeGet("colors.".concat(props.hoverBg));
}, ";}&:focus{outline:none;box-shadow:inset 0 0 0 1px ", function (props) {
  return themeGet("colors.".concat(props.focusBorderColor), props.focusBorderColor);
}, ";}&:active&:not(:disabled){transform:translateY(1px);}" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"));

BaseButton.defaultProps = {
  type: 'button',
  fontWeight: 'bold',
  fontSize: 'body',
  fontFamily: 'default',
  borderRadius: 'small',
  borderWidth: 1,
  borderColor: 'transparent',
  bg: 'primary',
  color: 'white',
  focusBorderColor: 'white',
  textDecoration: 'none',
  variant: VARIANTS.DEFAULT,
  'data-testid': 'button'
};

var getSize = function getSize(size) {
  switch (size) {
    case SIZES.MINI:
      return {
        lineHeight: '12px',
        px: 1,
        py: 1,
        fontSize: 'small'
      };

    case SIZES.SMALL:
      return {
        lineHeight: '16px',
        px: 1,
        py: 1
      };

    case SIZES.LARGE:
      return {
        lineHeight: '30px',
        px: 3,
        py: 1
      };

    case SIZES.MEDIUM:
    default:
      return {
        lineHeight: 'body',
        px: 2,
        py: 1
      };
  }
};

var getVariant = function getVariant(variant) {
  switch (variant) {
    case VARIANTS.PRIMARY:
      return {
        bg: 'primary',
        color: 'white'
      };

    case VARIANTS.OUTLINE:
      return {
        bg: 'transparent',
        color: 'text',
        borderColor: 'extended.neutrals.N700',
        focusBorderColor: 'extended.neutrals.N700'
      };

    case VARIANTS.TRANSPARENT:
      return {
        bg: 'transparent',
        color: 'text',
        borderColor: 'transparent',
        focusBorderColor: 'subduedText',
        hoverBg: 'muted'
      };

    case VARIANTS.DESTRUCTIVE:
      return {
        bg: 'warning',
        borderColor: 'warning',
        color: 'black'
      };

    case VARIANTS.LINK:
      return {
        bg: 'transparent',
        borderColor: 'transparent',
        color: 'accent',
        fontWeight: 'normal',
        hoverBg: 'subtle',
        focusBorderColor: 'accent'
      };

    case VARIANTS.SUBTLE:
      return {
        bg: 'subtle',
        borderColor: 'transparent',
        color: 'accent',
        focusBorderColor: 'accent'
      };

    case VARIANTS.DEFAULT:
    default:
      return {
        bg: 'muted',
        color: 'text'
      };
  }
};

var _ref2 =
/*#__PURE__*/
React.createElement(Spinner, {
  size: "tinier",
  color: "currentColor",
  isIndeterminate: true
});

var Button = forwardRef(function (_ref, ref) {
  var isLoading = _ref.isLoading,
      size = _ref.size,
      variant = _ref.variant,
      disabled = _ref.disabled,
      children = _ref.children,
      restProps = _objectWithoutProperties(_ref, ["isLoading", "size", "variant", "disabled", "children"]);

  return React.createElement(BaseButton, _extends({
    ref: ref,
    disabled: isLoading || disabled
  }, getSize(size), getVariant(variant), restProps), React.createElement("span", {
    style: {
      transition: 'opacity 300ms',
      opacity: isLoading ? 0 : 1
    }
  }, children), isLoading && React.createElement(Box, {
    as: "span",
    position: "absolute",
    lineHeight: 1,
    top: "50%",
    left: "50%",
    style: {
      transform: 'translate(-50%, -50%)'
    }
  }, _ref2));
});
Button.displayName = 'Button';
Button.propTypes = {
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(values(SIZES)),
  textDecoration: PropTypes.string,
  variant: PropTypes.oneOf(values(VARIANTS)),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired
};
Button.defaultProps = {
  size: SIZES.MEDIUM
};
Button.styledFunctions = getStyledFunctionsNames(styledFunctions);
export default Button;